import React from 'react';
import { motion } from 'framer-motion';
import './About.css';

const About: React.FC = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    initial: {},
    whileInView: {
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  return (
    <section id="about" className="about section">
      <div className="container">
        <motion.div 
          className="about-content"
          variants={staggerContainer}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true }}
        >
          <motion.h2 
            className="section-title neon-text animate-glow"
            variants={fadeInUp}
          >
            Sobre IMMOACTIVAT I ASSOCIATS SL
          </motion.h2>

          <motion.div 
            className="about-intro"
            variants={fadeInUp}
          >
            <p>
              Desde que iniciamos nuestra aventura en el mundo tecnológico, siempre hemos sentido una gran fascinación 
              por la innovación, la seguridad informática y el constante desafío que representa la tecnología. 
              En <strong className="code-text">IMMOACTIVAT</strong>, nos especializamos en crear soluciones digitales 
              seguras e innovadoras que impulsan el éxito de nuestros clientes.
            </p>
            
            <p>
              Nuestro enfoque se centra especialmente en el ámbito de la <span className="highlight">ciberseguridad</span>, 
              donde profundizamos en vulnerabilidades, exploits y métodos para proteger y fortalecer sistemas digitales. 
              Cada proyecto es una oportunidad de aplicar las mejores prácticas de seguridad desde el diseño hasta la implementación.
            </p>
          </motion.div>

          <motion.div 
            className="expertise-grid"
            variants={staggerContainer}
          >
            <motion.div className="expertise-card" variants={fadeInUp}>
              <div className="expertise-icon">🛡️</div>
              <h3>Ciberseguridad</h3>
              <p>
                Especialistas en análisis de vulnerabilidades, pentesting y fortalecimiento de sistemas. 
                Protegemos tu infraestructura digital con las últimas técnicas de seguridad.
              </p>
            </motion.div>

            <motion.div className="expertise-card" variants={fadeInUp}>
              <div className="expertise-icon">📱</div>
              <h3>Desarrollo Móvil</h3>
              <p>
                Creamos aplicaciones móviles nativas para Android e iOS con enfoque en seguridad, 
                rendimiento y experiencia de usuario excepcional.
              </p>
            </motion.div>

            <motion.div className="expertise-card" variants={fadeInUp}>
              <div className="expertise-icon">💻</div>
              <h3>Desarrollo Web</h3>
              <p>
                Aplicaciones web modernas y sitios corporativos con tecnologías de vanguardia, 
                diseño responsive y arquitectura escalable.
              </p>
            </motion.div>

            <motion.div className="expertise-card" variants={fadeInUp}>
              <div className="expertise-icon">🤖</div>
              <h3>Inteligencia Artificial</h3>
              <p>
                Implementamos soluciones de IA y Machine Learning para automatizar procesos 
                y crear experiencias inteligentes que impulsan tu negocio.
              </p>
            </motion.div>

            <motion.div className="expertise-card" variants={fadeInUp}>
              <div className="expertise-icon">🔐</div>
              <h3>Privacidad de Datos</h3>
              <p>
                Garantizamos el cumplimiento de normativas como GDPR y implementamos 
                sistemas de protección de datos de última generación.
              </p>
            </motion.div>

            <motion.div className="expertise-card" variants={fadeInUp}>
              <div className="expertise-icon">⚡</div>
              <h3>Optimización</h3>
              <p>
                Mejoramos el rendimiento de aplicaciones existentes y optimizamos 
                sistemas para máxima eficiencia y escalabilidad.
              </p>
            </motion.div>
          </motion.div>

          <motion.div 
            className="mission-statement"
            variants={fadeInUp}
          >
            <div className="mission-content glass-card">
              <h3 className="neon-text">Nuestra Misión</h3>
              <p>
                En <strong>IMMOACTIVAT I ASSOCIATS SL</strong>, nos dedicamos a transformar ideas en realidades digitales seguras. 
                Creemos que la tecnología debe ser un aliado confiable que proteja la privacidad y potencie las capacidades 
                de nuestros clientes. Por eso, cada línea de código que escribimos está diseñada pensando en la seguridad, 
                la escalabilidad y la innovación.
              </p>
              <div className="mission-values">
                <div className="value-item">
                  <span>Innovación Constante</span>
                </div>
                <div className="value-item">
                  <span>Seguridad Primero</span>
                </div>
                <div className="value-item">
                  <span>Compromiso Total</span>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="tech-philosophy"
            variants={fadeInUp}
          >
            <h3 className="gradient-text">Filosofía Tecnológica</h3>
            <div className="philosophy-grid">
              <div className="philosophy-item">
                <span className="philosophy-number">01</span>
                <div className="philosophy-content">
                  <h4>Privacy by Design</h4>
                  <p>La privacidad no es una característica añadida, es el fundamento de cada solución que desarrollamos.</p>
                </div>
              </div>
              
              <div className="philosophy-item">
                <span className="philosophy-number">02</span>
                <div className="philosophy-content">
                  <h4>Código Limpio</h4>
                  <p>Escribimos código mantenible, escalable y bien documentado que facilita futuras mejoras y actualizaciones.</p>
                </div>
              </div>
              
              <div className="philosophy-item">
                <span className="philosophy-number">03</span>
                <div className="philosophy-content">
                  <h4>Tecnología Moderna</h4>
                  <p>Utilizamos las últimas tecnologías y frameworks para garantizar soluciones futuras y eficientes.</p>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
